<template>
  <div class="min-h-screen items-start pt-24 px-5 md:px-10">
    <div class="font-black text-black text-2xl py-3">Employer Not Found</div>
    <div class="bg-white shadow-lg rounded-lg p-3">
      <div class="text-black text-lg">
        Uh oh! It looks like we had trouble finding your employer. Please provide your information
        below to see if we can service your employer.
      </div>

      <form autocomplete="off" class="w-full py-3 mt-4" novalidate @submit.prevent="next">
        <Input
          v-for="item in fieldOrder"
          v-bind:key="item"
          v-model="personalInformation[item].value"
          :error="personalInformation[item].error"
          :getter="$getters[item]"
          :maxlength="personalInformation[item].maxlength"
          :name="personalInformation[item].name"
          :id="personalInformation[item].id"
          :placeholder="personalInformation[item].placeholder"
          :prefix="['phone_number'].includes(item) ? '+1' : ''"
          :setter="$setters[item]"
          :type="['phone_number'].includes(item) ? 'tel' : 'text'"
          :underlined="true"
          class="mb-3"
        />
        <Dropdown
          v-model="personalInformation.industry.value"
          v-bind:dropdownOptions="industries"
          :value="personalInformation.industry.value"
          :error="personalInformation.industry.error"
          :placeholder="personalInformation.industry.placeholder"
          :name="personalInformation.industry.name"
          type="text"
          :underlined="true"
          inputClass="mt-4 sm:mt-0"
          :id="personalInformation.industry.id"
          class="mb-3"
        />
        <Dropdown
          v-model="personalInformation.multipleDDAccounts.value"
          v-bind:dropdownOptions="['Yes', 'No']"
          :value="personalInformation.multipleDDAccounts.value"
          :error="personalInformation.multipleDDAccounts.error"
          :placeholder="personalInformation.multipleDDAccounts.placeholder"
          :name="personalInformation.multipleDDAccounts.name"
          type="text"
          :underlined="true"
          inputClass="mt-4 sm:mt-0"
          :id="personalInformation.multipleDDAccounts.id"
          class="mb-3"
        />

        <Dropdown
          v-model="personalInformation.directDepositUpdateDescription.value"
          v-bind:dropdownOptions="[
            'Paper form',
            'Online / Employee Self Service portal',
            'In person',
            'I don\'t know'
          ]"
          :value="personalInformation.directDepositUpdateDescription.value"
          :error="personalInformation.directDepositUpdateDescription.error"
          :placeholder="personalInformation.directDepositUpdateDescription.placeholder"
          :name="personalInformation.directDepositUpdateDescription.name"
          type="text"
          :underlined="true"
          inputClass="mt-4 sm:mt-0"
          :id="personalInformation.directDepositUpdateDescription.id"
          class="mb-3"
        />
      </form>
      <div class="flex mt-2 justify-end">
        <button
          type="primary ml-auto"
          class="btn base-button btn-primary ml-auto"
          :disabled="!nextEnabled || nextLoading"
          aria-label="navigate next"
          @click="sendNewEmployerRequest()"
        >
          Submit Request
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// import { mapFields } from 'vuex-map-fields';
import gql from 'graphql-tag'
import Vue from 'vue'
import Engine from '@/mixins/Engine'
import Swal from 'sweetalert2'
import Dropdown from '@/components/VIVA/Dropdown.vue'

export default {
  name: 'EmployerRequest',
  mixins: [Engine],
  props: {},
  components: {
    Dropdown
  },
  data: function () {
    return {
      nextLoading: false,
      fieldOrder: [
        'given_name',
        'family_name',
        'work_email',
        'personal_email',
        'phone_number',
        'employer'
      ],
      personalInformation: {
        given_name: {
          name: 'First name',
          placeholder: 'Jane',
          value: '',
          error: '',
          id: 'ner_first_name'
        },
        family_name: {
          name: 'Last name',
          placeholder: 'Doe',
          value: '',
          error: '',
          id: 'ner_family_name'
        },
        personal_email: {
          name: 'Personal email',
          placeholder: 'Alex@gmail.com',
          value: '',
          error: '',
          id: 'ner_personal_email'
        },
        work_email: {
          name: 'Work email',
          placeholder: 'Jane@vivafinance.com',
          value: '',
          error: '',
          id: 'ner_work_email'
        },
        phone_number: {
          name: 'Cell phone number',
          maxlength: 14,
          placeholder: '(XXX) XXX-XXXX',
          value: '',
          error: '',
          id: 'ner_phone_number'
        },
        employer: {
          name: 'Employer',
          placeholder: 'VIVA Finance',
          value: '',
          error: '',
          id: 'ner_employer'
        },
        industry: {
          name: 'Industry',
          placeholder: 'Education',
          value: '',
          error: '',
          id: 'ner_industry'
        },
        directDepositUpdateDescription: {
          name: 'How do you update your direct deposit information to receive your paycheck? (Optional)',
          placeholder: '',
          value: '',
          error: '',
          optional: true,
          id: 'ner_dd_description'
        },
        multipleDDAccounts: {
          name: 'Does your company allow for you to send your paycheck to multiple accounts?',
          placeholder: '',
          value: '',
          error: '',
          id: 'ner_dd_multiple_accounts'
        }
      },
      vivaIndustries: [
        'Accommodation and Food Services',
        'Administrative and Waste Services',
        'Early Education',
        'Government - Federal',
        'Government - Local',
        'Government - State',
        'Healthcare',
        'Higher Education',
        'K-12 Education',
        'Pest Control',
        'Power and Utilities'
      ],
      qsIndustries: [
        'Agriculture, Forestry, Fishing and Hunting',
        'Arts, Entertainment and Recreation',
        'Construction',
        'Finance and Insurance',
        'Information',
        'Management of Companies and Enterprises',
        'Manufacturing',
        'Mining',
        'Professional, Scientific, and Technical Services',
        'Real Estate and Rental Leasing',
        'Retail Trade',
        'Transportation and Warehousing',
        'Wholesale Trade'
      ]
    }
  },
  computed: {
    personalEmail() {
      return this.personalInformation.personal_email.value
    },
    workEmail() {
      return this.personalInformation.work_email.value
    },
    phoneNumber() {
      return this.personalInformation.phone_number.value
    },
    firstName() {
      return this.personalInformation.given_name.value
    },
    lastName() {
      return this.personalInformation.family_name.value
    },
    employerName() {
      return this.personalInformation.employer.value
    },
    industry() {
      return this.personalInformation.industry.value
    },
    directDepositUpdateDescription() {
      return this.personalInformation.directDepositUpdateDescription.value
    },
    multipleDDAccounts() {
      return this.personalInformation.multipleDDAccounts.value
    },

    fieldsValid() {
      const errorsExist = Object.keys(this.personalInformation).filter(
        (fn) => this.personalInformation[fn].error.length > 0
      )
      const emptyRequired = Object.keys(this.personalInformation).filter(
        (fieldName) =>
          !this.personalInformation[fieldName].optional &&
          this.personalInformation[fieldName].value.length === 0
      )
      return errorsExist.length + emptyRequired.length === 0
    },
    nextEnabled() {
      return this.fieldsValid
    },
    industries() {
      const allIndustries = [...this.vivaIndustries, ...this.qsIndustries].sort()

      return ['--Other--', ...allIndustries]
    }
  },
  // mounted() { //for testing
  //   this.personalInformation.given_name.value = 'Alex'
  //   this.personalInformation.family_name.value = 'Ketchum'
  //   this.personalInformation.work_email.value = 'alex@viva-finance.com'
  //   this.personalInformation.personal_email.value = 'alexketchum94@gmail.com'
  //   this.personalInformation.phone_number.value = '2514243165'
  //   this.personalInformation.multipleDDAccounts.value = 'Yes'
  //   this.personalInformation.employer.value = 'VIVA'
  //   this.personalInformation.industry.value = 'Alex'
  // },
  methods: {
    sendNewEmployerRequest() {
      let requestParams = ''
      this.nextLoading = true
      for (const [key, value] of Object.entries(this.personalInformation)) {
        if (key === 'multipleDDAccounts') {
          let bool
          if (value.value === 'Yes') {
            bool = true
          } else {
            bool = false
          }
          requestParams += `${key} : ${bool}, `
        } else {
          requestParams += `${key} : "${value.value}", `
        }
      }
      Vue.prototype.$apiClientKey
        .mutate({
          mutation: gql(`mutation {
              addNewEmployerRequest(${requestParams})
            }`)
        })
        .then(() => {
          const isVivaLoan = this.isVivaIndustry()

          this.$store.commit('setNerIndustryType', isVivaLoan)

          new Promise((res, rej) => {
            if (isVivaLoan) return res()

            Vue.prototype.$apiClientKey
              .mutate({
                mutation: gql(
                  `mutation { inTheHoleActionTaken(workEmail: "${this.personalInformation.work_email.value}") }`
                )
              })
              .then(res)
              .catch(rej)
          }).then(() => {
            this.$router.push('nersubmitted')
          })
        })
        .catch((e) => {
          console.log(e)
          Swal.fire({
            title: 'Your request has been could not be recorded.',
            confirmButtonColor: '#69D393',
            text: 'An error occurred and we could not record your response. Please reach out to customer support for more help.',
            icon: 'error',
            confirmButtonText: `Ok`
          })
        })
        .finally(() => {
          this.nextLoading = false
        })
    },
    isVivaIndustry() {
      const bool = [...this.vivaIndustries, '--Other--'].includes(
        this.personalInformation.industry.value
      )
      console.log('bool: ', bool)
      return bool
    }
  },
  // set the initial values for the page.  probably from the store.
  watch: {
    personalEmail: {
      handler(v) {
        if (!v.includes('@') || !v.includes('.')) {
          this.personalInformation.personal_email.error =
            'Please enter a valid personal email address'
        } else {
          this.personalInformation.personal_email.error = ''
        }
      }
    },
    workEmail: {
      handler(v) {
        if (!v.includes('@') || !v.includes('.')) {
          this.personalInformation.work_email.error = 'Please enter a valid personal email address'
        } else {
          this.personalInformation.work_email.error = ''
        }
      }
    },
    phoneNumber: {
      handler(newValue, oldValue) {
        if (oldValue !== '') {
          this.phoneChanged = true
        }
        if (!newValue.match(/^\d{10}$/)) {
          this.personalInformation.phone_number.error = 'Please enter a valid phone number.'
        } else if (this.homePhone === this.phoneNumber) {
          this.personalInformation.phone_number.error = ''
          this.personalInformation.home_phone_number.error =
            'Home phone cannot be the same as cell phone. Change home phone or leave blank.'
        } else {
          this.personalInformation.phone_number.error = ''
        }
      }
    },
    firstName: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.given_name.error = 'Please enter your first name.'
        } else {
          this.personalInformation.given_name.error = ''
        }
      }
    },
    lastName: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.family_name.error = 'Please enter your last name.'
        } else {
          this.personalInformation.family_name.error = ''
        }
      }
    },
    employerName: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.employer.error = 'Please enter your employer.'
        } else {
          this.personalInformation.employer.error = ''
        }
      }
    },
    multipleDDAccounts: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.multipleDDAccounts.error = 'Please choose an option.'
        } else {
          this.personalInformation.multipleDDAccounts.error = ''
        }
      }
    },
    industry: {
      handler(v) {
        if (v.length < 1) {
          this.personalInformation.industry.error = 'Please enter your Industry.'
        } else {
          this.personalInformation.industry.error = ''
        }
      }
    }
  }
}
</script>
